import { AxiosInstance } from 'axios';
import { result, Result } from 'util/result';
import Terra from '../Terra';
import {
  ModuleIntegrationConfigResponse,
  ModuleIntegrationConfigsResponse,
} from 'data/terra/api/terra/response/ModuleIntegrationConfigResponse';
import { pickBy } from 'lodash';
import {
  ModuleIntegrationResponse,
  ModuleIntegrationsResponse,
} from 'data/terra/api/terra/response/ModuleIntegrationResponse';
import { ModuleIntegrationConfigDescription } from 'data/terra/ModuleConfig';
import * as ModuleUtil from '../util/ModuleUtil';

class ModuleIntegrationEndpoint {
  getIntegrationsUrl = () =>
    `${Terra.TERRA_HOST}/${Terra.TERRA_BASE_API_URL}/module-integrations`;

  getIntegrationsDetailUrl = (integrationId: string) =>
    `${Terra.TERRA_HOST}/${Terra.TERRA_BASE_API_URL}/module-integrations/${integrationId}`;

  getIntegrationConfigsUrl = (integrationId: string) =>
    `${Terra.TERRA_HOST}/${Terra.TERRA_BASE_API_URL}/module-integrations/${integrationId}/configs`;

  getIntegrationConfigDetailUrl = (integrationId: string, configId: string) =>
    `${Terra.TERRA_HOST}/${Terra.TERRA_BASE_API_URL}/module-integrations/${integrationId}/configs/${configId}`;
}

export class ModuleIntegrationApi {
  terraClient: AxiosInstance;
  endpoint = new ModuleIntegrationEndpoint();

  constructor(client: AxiosInstance) {
    this.terraClient = client;
  }

  createIntegration(
    appId: string,
    appVersionId: string,
    moduleId: string,
    moduleVersionId: string
  ): Promise<Result<ModuleIntegrationResponse>> {
    return result(
      async () =>
        (
          await this.terraClient.post<ModuleIntegrationResponse>(
            this.endpoint.getIntegrationsUrl(),
            {
              app: {
                id: appId,
              },
              appVersion: {
                id: appVersionId,
              },
              module: {
                id: moduleId,
              },
              moduleVersion: {
                id: moduleVersionId,
              },
            }
          )
        ).data
    );
  }

  fetchIntegrations(
    appId?: string,
    appVersionId?: string,
    moduleId?: string,
    moduleVersionId?: string
  ): Promise<Result<ModuleIntegrationsResponse>> {
    return result(
      async () =>
        (
          await this.terraClient.get<ModuleIntegrationsResponse>(
            this.endpoint.getIntegrationsUrl(),
            {
              params: pickBy(
                {
                  appId,
                  appVersionId: appVersionId,
                  moduleId,
                  moduleVersionId: moduleVersionId,
                  pageSize: 50,
                },
                v => v !== undefined && v !== null
              ),
            }
          )
        ).data
    );
  }

  deleteIntegration(integrationId: string): Promise<Result<boolean>> {
    return result(async () => {
      await this.terraClient.delete<void>(
        this.endpoint.getIntegrationsDetailUrl(integrationId)
      );
      return true;
    });
  }

  fetchIntegrationConfigs(
    integrationId: string
  ): Promise<Result<ModuleIntegrationConfigsResponse>> {
    return result(
      async () =>
        (
          await this.terraClient.get<ModuleIntegrationConfigsResponse>(
            this.endpoint.getIntegrationConfigsUrl(integrationId),
            {
              params: {
                pageSize: 50,
              },
            }
          )
        ).data
    );
  }

  createIntegrationConfig(
    integrationId: string,
    config: ModuleIntegrationConfigDescription<any, any>,
    envId: string
  ): Promise<Result<ModuleIntegrationConfigResponse>> {
    return result(
      async () =>
        (
          await this.terraClient.post<ModuleIntegrationConfigResponse>(
            this.endpoint.getIntegrationConfigsUrl(integrationId),
            {
              ...ModuleUtil.normalize(config),
              env: {
                id: envId,
              },
            }
          )
        ).data
    );
  }

  updateIntegrationConfig(
    integrationId: string,
    configId: string,
    config: ModuleIntegrationConfigDescription<any, any>
  ): Promise<Result<ModuleIntegrationConfigResponse>> {
    return result(
      async () =>
        (
          await this.terraClient.put<ModuleIntegrationConfigResponse>(
            this.endpoint.getIntegrationConfigDetailUrl(
              integrationId,
              configId
            ),
            ModuleUtil.normalize(config)
          )
        ).data
    );
  }
}
